import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Drawer, Tabs } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import SprTovCard, { TOV_TAB_ITEMS, TOV_TAB_KEYS } from "./spr-tov-card";
import "./spr-tov-drawer.css";

const DRAWER_WIDTH_KEY = "planOnline.sprTov.cardDrawer.width";
const MIN_WIDTH = 480;
const MAX_WIDTH = 1100;
const DEFAULT_WIDTH = 560;
const SCREEN_MARGIN = 100;

const maxAllowedWidth = () => {
  if (typeof window === "undefined") return MAX_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - SCREEN_MARGIN));
};

const clampWidth = (value) => {
  const max = maxAllowedWidth();
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(DEFAULT_WIDTH, max);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

/**
 * Карточка группы / услуги (ядро Delphi TFrmETovGroup / TFrmESprTovSan).
 */
const SprTovDrawer = ({
  open,
  idTov,
  isNew = false,
  isGroupNew = false,
  isGroup = false,
  idUpper,
  parentOptions = [],
  onClose,
  onSaved
}) => {
  const [size, setSize] = useState(loadWidth);
  const [activeTab, setActiveTab] = useState(TOV_TAB_KEYS.MAIN);

  const onlyMainTab = isNew || isGroupNew || isGroup;

  useEffect(() => {
    if (open) setActiveTab(TOV_TAB_KEYS.MAIN);
  }, [open, idTov, isNew]);

  useEffect(() => {
    if (onlyMainTab && activeTab !== TOV_TAB_KEYS.MAIN) {
      setActiveTab(TOV_TAB_KEYS.MAIN);
    }
  }, [onlyMainTab, activeTab]);

  useEffect(() => {
    if (!open) return undefined;
    const onWinResize = () => setSize((prev) => clampWidth(prev));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const drawerMaxSize = maxAllowedWidth();

  const tabItems = useMemo(() => {
    if (onlyMainTab) {
      return TOV_TAB_ITEMS.filter((item) => item.key === TOV_TAB_KEYS.MAIN);
    }
    return TOV_TAB_ITEMS;
  }, [onlyMainTab]);

  const onTabChange = useCallback(
    (key) => {
      if (onlyMainTab && key !== TOV_TAB_KEYS.MAIN) return;
      setActiveTab(key);
    },
    [onlyMainTab]
  );

  const title = (
    <div className="spr-tov-drawer__title">
      <Tabs
        className="spr-tov-drawer__tabs"
        size="small"
        activeKey={activeTab}
        onChange={onTabChange}
        items={tabItems}
      />
    </div>
  );

  return (
    <Drawer
      placement="right"
      title={title}
      size={size}
      maxSize={drawerMaxSize}
      open={open}
      onClose={onClose}
      destroyOnClose
      closable={false}
      rootClassName="spr-tov-drawer"
      extra={
        <Button
          type="text"
          className="spr-tov-drawer__close"
          icon={<CloseOutlined />}
          aria-label="Закрыть"
          title="Закрыть"
          onClick={onClose}
        />
      }
      styles={{
        body: { display: "flex", flexDirection: "column", paddingTop: 12 }
      }}
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      {open && (idTov != null || isNew) ? (
        <SprTovCard
          idTov={idTov}
          isNew={isNew}
          isGroupNew={isGroupNew}
          idUpper={idUpper}
          open={open}
          activeTab={activeTab}
          parentOptions={parentOptions}
          onSaved={onSaved}
          onCancel={onClose}
        />
      ) : null}
    </Drawer>
  );
};

export default SprTovDrawer;
